import { useState, useMemo } from "react";
import Eyebrow from "../shared/Eyebrow";
import RevealText from "../shared/RevealText";
import BeforeAfterSlider from "../shared/BeforeAfterSlider";
import { GALLERY_CATEGORIES } from "../../data/media";
import { mediaService } from "../../services/mediaService";
import { useRemoteData } from "../../hooks/useRemoteData";
import AsyncSection from "../shared/AsyncSection";
import { cn } from "../../lib/utils";

export default function BeforeAfterGallery() {
  const [cat, setCat] = useState("All");

  const { data: pairs, loading } = useRemoteData(() => mediaService.beforeAfter(), [], {
    initialValue: [],
  });

  const items = useMemo(
    () =>
      (pairs ?? []).filter(
        (b) => b.active !== false && (cat === "All" || b.serviceCategory === cat)
      ),
    [pairs, cat]
  );

  return (
    <section
      className="bg-ink py-24 md:py-32 border-t border-charcoal/50"
      data-testid="before-after-gallery"
    >
      <div className="container-emilio">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <Eyebrow>Before & After</Eyebrow>
            <RevealText
              as="h2"
              text={"Real work.\nVisible results."}
              className="mt-4 font-display font-extrabold text-white text-display"
            />
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-2">
            {GALLERY_CATEGORIES.slice(0, 5).map((c) => (
              <button
                type="button"
                key={c}
                data-testid={`gallery-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setCat(c)}
                className={cn(
                  "text-xs uppercase tracking-[0.18em] px-3 py-2 border transition-colors",
                  cat === c
                    ? "border-emerald text-emerald"
                    : "border-charcoal text-mutedtext hover:text-white hover:border-white/40"
                )}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <AsyncSection
          loading={loading}
          isEmpty={items.length === 0}
          skeleton={
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" aria-hidden="true">
              {[0, 1].map((index) => (
                <div key={index} className="animate-pulse">
                  <div className="aspect-[4/3] bg-charcoal/40 border border-charcoal" />
                  <div className="mt-4 h-4 w-1/2 bg-charcoal/40 rounded" />
                </div>
              ))}
            </div>
          }
          emptyTitle="No items in this category yet"
          emptyMessage="Try another category, or check back soon."
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {items.map((b) => (
              <div key={b.id}>
                <BeforeAfterSlider
                  beforeSrc={b.beforeImageUrl}
                  afterSrc={b.afterImageUrl}
                  alt={b.altText || b.title}
                  testId={`ba-${b.id}`}
                />
                <div className="mt-4 flex items-center justify-between text-sm">
                  <div className="text-white font-display font-semibold">{b.title}</div>
                  <div className="text-mutedtext text-xs uppercase tracking-[0.16em]">
                    {b.serviceCategory}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </AsyncSection>
      </div>
    </section>
  );
}
